import { useEffect, useRef } from 'react';
import './BrandSplash.css';

/**
 * Link my Food — animated brand splash.
 *
 * The story is the brand idea: two chain links draw themselves and lock together, a fork and
 * spoon join either side, the wordmark rises letter by letter (`my` in orange), the tagline
 * settles, and the five experience colours fill a short progress line. Pure SVG + CSS, so it
 * can be ported 1:1 to the mobile apps (the timeline below is the shared spec).
 *
 * Timeline (ms): 0 glow · 100 orange link · 450 navy link · 1250 lock + ripple ·
 * 1250 fork/spoon · 1500 wordmark · 2150 tagline · 2350 tier line · ~3000 done.
 *
 * Props: `onDone` is called once after the timeline; `dark` swaps to the navy surface;
 * `speed` scales the whole timeline (1 = normal). Honors prefers-reduced-motion by showing the
 * finished lockup with a short fade instead of the drawing sequence.
 */
const NAVY = '#02142C';
const ORANGE = '#FF7300';
export const TIER_COLOURS = ['#3B6801', '#004177', '#3B1771', '#EC7E03', '#021325'];
const TOTAL_MS = 3000;

const Letters = ({ text, start, className }) =>
  [...text].map((char, index) => (
    <span key={index} className={`bsplash-letter ${className || ''}`} style={{ '--d': `${start + index * 45}ms` }} aria-hidden="true">
      {char}
    </span>
  ));

export default function BrandSplash({ onDone, dark = false, speed = 1, replayKey = 0 }) {
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const timer = setTimeout(() => done.current?.(), TOTAL_MS / speed);
    return () => clearTimeout(timer);
  }, [speed, replayKey]);

  const ink = dark ? '#FFFFFF' : NAVY;
  return (
    <div key={replayKey} className={`bsplash${dark ? ' is-dark' : ''}`} style={{ '--speed': speed, '--ink': ink }} role="img" aria-label="Link my Food. One link. Every food experience.">
      <div className="bsplash-glow" aria-hidden="true" />
      <div className="bsplash-stage" aria-hidden="true">
        <div className="bsplash-mark">
          <span className="bsplash-ripple" />
          <svg viewBox="0 0 140 136" focusable="false">
            <g className="bsplash-fork" fill={ink}>
              <rect x="4.4" y="33" width="3.2" height="27" rx="1.6" />
              <rect x="9.4" y="33" width="3.2" height="27" rx="1.6" />
              <rect x="14.4" y="33" width="3.2" height="27" rx="1.6" />
              <path d="M4.4 57h13.2v4.4a6.6 6.6 0 0 1-13.2 0z" />
              <path d="M9.2 67h3.6l-.72 48a1 1 0 0 1-2.16 0z" />
            </g>
            <g className="bsplash-links">
              <rect className="bsplash-draw bsplash-draw-a" pathLength="1" x="28" y="4" width="48" height="88" rx="24" fill="none" stroke={ORANGE} strokeWidth="8.5" />
              <rect className="bsplash-draw bsplash-draw-b" pathLength="1" x="52" y="42" width="48" height="88" rx="24" fill="none" stroke={ink} strokeWidth="8.5" />
              <path className="bsplash-lock" d="M28 68A24 24 0 0 0 52 92" fill="none" stroke={ORANGE} strokeWidth="8.5" strokeLinecap="round" />
            </g>
            <g className="bsplash-spoon" fill={ink}>
              <ellipse cx="124" cy="46" rx="10.2" ry="14.6" />
              <path d="M122.2 60h3.6l-.72 55a1 1 0 0 1-2.16 0z" />
            </g>
            <circle className="bsplash-dot bsplash-dot-a" cx="92" cy="13" r="4.8" fill={ORANGE} />
            <circle className="bsplash-dot bsplash-dot-b" cx="36" cy="110" r="5.2" fill={ink} />
          </svg>
        </div>

        <div className="bsplash-word">
          <span className="bsplash-line">
            <Letters text="Link" start={1500} className="is-ink" />
            <Letters text="my" start={1680} className="is-orange" />
            <Letters text="Food" start={1820} className="is-ink" />
          </span>
          <span className="bsplash-tagline">One link. Every food experience.</span>
        </div>

        <div className="bsplash-tiers">
          {TIER_COLOURS.map((colour, index) => (
            <i key={colour} style={{ '--c': colour, '--d': `${2350 + index * 110}ms` }} />
          ))}
        </div>
      </div>
    </div>
  );
}
